import { useId } from "react";
import { useTooltip } from "../Tooltip/Tooltip";
import { type InteractiveItem, isItemDisabled } from "./items";
import { MenuRowContent } from "./MenuRowContent";
import { rowChecked, rowClassName, rowRole } from "./rowAttributes";

interface MenuRowProps {
  item: InteractiveItem;
  ref: (el: HTMLDivElement | null) => void;
  /** Its submenu is open. */
  expanded: boolean;
  /** Id of the open submenu panel. */
  controls?: string;
  onActivate: () => void;
  onHover: () => void;
}

/** One interactive row; a disabled one stays focusable and explains why in a tooltip. */
export function MenuRow({ item, ref, expanded, controls, onActivate, onHover }: MenuRowProps): React.JSX.Element {
  const reasonId = useId();
  const { anchorProps, tooltip } = useTooltip(item.disabledReason ?? "", { disabled: !item.disabledReason });
  const isSubmenu = item.kind === "submenu";

  return (
    <div
      ref={ref}
      role={rowRole(item)}
      tabIndex={-1}
      aria-disabled={isItemDisabled(item) || undefined}
      aria-checked={rowChecked(item)}
      aria-haspopup={isSubmenu ? "menu" : undefined}
      aria-expanded={isSubmenu ? expanded : undefined}
      aria-controls={controls}
      aria-describedby={item.disabledReason ? reasonId : undefined}
      data-active={expanded || undefined}
      className={rowClassName(item)}
      onClick={onActivate}
      onMouseEnter={(e) => {
        e.currentTarget.focus({ preventScroll: true });
        onHover();
        anchorProps.onMouseEnter(e);
      }}
      onMouseLeave={anchorProps.onMouseLeave}
      onFocus={anchorProps.onFocus}
      onBlur={anchorProps.onBlur}
    >
      <MenuRowContent item={item} reasonId={reasonId} />
      {tooltip}
    </div>
  );
}
